import { supabase } from './supabase';

export const postsApi = {
    list: async (params?: Record<string, string>) => {
        let query = supabase
            .from('posts')
            .select(`
                *,
                post_categories(categories(id, name)),
                post_tags(tags(id, name))
            `, { count: 'exact' });

        if (params?.search) query = query.ilike('title', `%${params.search}%`);
        if (params?.status) query = query.eq('status', params.status);

        const page = parseInt(params?.page || '1');
        const limit = parseInt(params?.limit || '20');
        const from = (page - 1) * limit;
        const to = from + limit - 1;

        const { data, count, error } = await query
            .range(from, to)
            .order('created_at', { ascending: false });

        if (error) throw error;

        // Flatten junctions
        const posts = (data || []).map(post => ({
            ...post,
            categories: post.post_categories?.map((pc: any) => pc.categories) || [],
            tags: post.post_tags?.map((pt: any) => pt.tags) || []
        }));

        return { posts, total: count || 0, page, totalPages: Math.ceil((count || 0) / limit) };
    },

    get: async (id: string) => {
        const { data, error } = await supabase
            .from('posts')
            .select(`
                *,
                post_categories(categories(id, name)),
                post_tags(tags(id, name))
            `)
            .eq('id', id)
            .single();

        if (error) throw error;

        return {
            ...data,
            categories: data.post_categories?.map((pc: any) => pc.categories) || [],
            tags: data.post_tags?.map((pt: any) => pt.tags) || []
        };
    },

    create: async (data: any) => {
        const { categories, tags, ...postData } = data;

        const { data: post, error } = await supabase
            .from('posts')
            .insert([postData])
            .select()
            .single();

        if (error) throw error;

        // Junctions
        if (categories && categories.length > 0) {
            const { error: catErr } = await supabase.from('post_categories').insert(
                categories.map((catId: string) => ({ post_id: post.id, category_id: catId }))
            );
            if (catErr) throw catErr;
        }

        if (tags && tags.length > 0) {
            const { error: tagErr } = await supabase.from('post_tags').insert(
                tags.map((tagId: string) => ({ post_id: post.id, tag_id: tagId }))
            );
            if (tagErr) throw tagErr;
        }

        return post;
    },

    update: async (id: string, data: any) => {
        const { categories, tags, ...postData } = data;
        const { error: postError } = await supabase
            .from('posts')
            .update(postData)
            .eq('id', id);

        if (postError) throw postError;

        // Update Junctions
        if (categories !== undefined) {
            const { error: delCatErr } = await supabase.from('post_categories').delete().eq('post_id', id);
            if (delCatErr) throw delCatErr;

            if (categories.length) {
                const { error: insCatErr } = await supabase.from('post_categories').insert(
                    categories.map((catId: string) => ({ post_id: id, category_id: catId }))
                );
                if (insCatErr) throw insCatErr;
            }
        }
        if (tags !== undefined) {
            const { error: delTagErr } = await supabase.from('post_tags').delete().eq('post_id', id);
            if (delTagErr) throw delTagErr;

            if (tags.length) {
                const { error: insTagErr } = await supabase.from('post_tags').insert(
                    tags.map((tagId: string) => ({ post_id: id, tag_id: tagId }))
                );
                if (insTagErr) throw insTagErr;
            }
        }
    },

    delete: async (id: string) => {
        const { error } = await supabase.from('posts').delete().eq('id', id);
        if (error) throw error;
        return { ok: true };
    }
};

export const categoriesApi = {
    list: async () => {
        const { data, error } = await supabase.from('categories').select('*').order('name');
        if (error) throw error;
        return data || [];
    },
    create: async (data: any) => {
        const { data: cat, error } = await supabase.from('categories').insert([data]).select().single();
        if (error) throw error;
        return cat;
    },
    update: async (id: string, data: any) => {
        const { data: cat, error } = await supabase.from('categories').update(data).eq('id', id).select().single();
        if (error) throw error;
        return cat;
    },
    delete: async (id: string) => {
        const { error } = await supabase.from('categories').delete().eq('id', id);
        if (error) throw error;
        return { ok: true };
    }
};

export const tagsApi = {
    list: async () => {
        const { data, error } = await supabase.from('tags').select('*').order('name');
        if (error) throw error;
        return data || [];
    },
    create: async (data: any) => {
        const { data: tag, error } = await supabase.from('tags').insert([data]).select().single();
        if (error) throw error;
        return tag;
    },
    update: async (id: string, data: any) => {
        const { data: tag, error } = await supabase.from('tags').update(data).eq('id', id).select().single();
        if (error) throw error;
        return tag;
    },
    delete: async (id: string) => {
        const { error } = await supabase.from('tags').delete().eq('id', id);
        if (error) throw error;
        return { ok: true };
    }
};

export const pagesApi = {
    list: async (params?: Record<string, string>) => {
        let query = supabase.from('pages').select('*', { count: 'exact' });
        if (params?.search) query = query.ilike('title', `%${params.search}%`);
        if (params?.status) query = query.eq('status', params.status);

        const page = parseInt(params?.page || '1');
        const limit = parseInt(params?.limit || '20');
        const from = (page - 1) * limit;
        const to = from + limit - 1;

        const { data, count, error } = await query
            .range(from, to)
            .order('created_at', { ascending: false });

        if (error) throw error;
        return { pages: data || [], total: count || 0, page, totalPages: Math.ceil((count || 0) / limit) };
    },
    get: async (id: string) => {
        const { data, error } = await supabase.from('pages').select('*').eq('id', id).single();
        if (error) throw error;
        return data;
    },
    getBySlug: async (slug: string) => {
        const { data, error } = await supabase
            .from('pages')
            .select('*')
            .eq('slug', slug)
            .eq('status', 'published')
            .maybeSingle();

        if (error) throw error;
        return data;
    },
    create: async (data: any) => {
        const { data: page, error } = await supabase.from('pages').insert([data]).select().single();
        if (error) throw error;
        return page;
    },
    update: async (id: string, data: any) => {
        const { error } = await supabase.from('pages').update(data).eq('id', id);
        if (error) throw error;
    },
    delete: async (id: string) => {
        const { error } = await supabase.from('pages').delete().eq('id', id);
        if (error) throw error;
        return { ok: true };
    }
};

export const mediaApi = {
    list: async () => {
        const { data, error } = await supabase.from('media').select('*').order('created_at', { ascending: false });
        if (error) throw error;
        return data || [];
    },
    upload: async (file: File, category: string = 'ogólne') => {
        const fileExt = file.name.split('.').pop();
        const fileName = `${Math.random().toString(36).substring(2)}.${fileExt}`;
        const filePath = `${fileName}`;

        const { error: uploadError } = await supabase.storage
            .from('media')
            .upload(filePath, file);

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
            .from('media')
            .getPublicUrl(filePath);

        const { data: media, error } = await supabase
            .from('media')
            .insert([{
                filename: fileName,
                original_name: file.name,
                mime_type: file.type,
                size: file.size,
                url: publicUrl,
                category
            }])
            .select()
            .single();

        if (error) throw error;
        return media;
    },
    delete: async (id: string) => {
        const { data: med } = await supabase.from('media').select('filename').eq('id', id).single();
        if (med?.filename) {
            await supabase.storage.from('media').remove([med.filename]);
        }
        const { error } = await supabase.from('media').delete().eq('id', id);
        if (error) throw error;
        return { ok: true };
    },
    update: async (id: string, data: any) => {
        const { error } = await supabase
            .from('media')
            .update(data)
            .eq('id', id);
        if (error) throw error;
        return { ok: true };
    },
    cleanupAvatars: async () => {
        // 1. Fetch all media that are in the "avatars" category
        const { data: avatarMedia, error: mediaErr } = await supabase
            .from('media')
            .select('id, url, filename')
            .eq('category', 'avatars');

        if (mediaErr) throw mediaErr;
        if (!avatarMedia || avatarMedia.length === 0) return { deletedCount: 0 };

        // 2. Fetch all current profile avatars
        const { data: profiles, error: profErr } = await supabase
            .from('profiles')
            .select('avatar_url')
            .not('avatar_url', 'is', null);

        if (profErr) throw profErr;

        const usedUrls = new Set(profiles?.map(p => p.avatar_url) || []);

        // 3. Find unused avatars
        const unusedMedia = avatarMedia.filter(m => !usedUrls.has(m.url));
        if (unusedMedia.length === 0) return { deletedCount: 0 };

        // 4. Delete from storage and database
        const filesToRemove = unusedMedia.map(m => m.filename);
        await supabase.storage.from('media').remove(filesToRemove);

        const idsToRemove = unusedMedia.map(m => m.id);
        const { error: delErr } = await supabase
            .from('media')
            .delete()
            .in('id', idsToRemove);

        if (delErr) throw delErr;

        return { deletedCount: idsToRemove.length };
    }
};

export const settingsApi = {
    get: async () => {
        const { data, error } = await supabase.from('settings').select('*');
        if (error) throw error;
        const settings: Record<string, string> = {};
        data?.forEach(s => settings[s.key] = s.value);
        return settings;
    },
    save: async (data: Record<string, string>) => {
        const updates = Object.entries(data).map(([key, value]) => ({ key, value }));
        const { error } = await supabase.from('settings').upsert(updates);
        if (error) throw error;
        return data;
    }
};

export const communityApi = {
    list: async (parentId: string | null = null) => {
        let query = supabase
            .from('community_posts')
            .select(`
                *,
                profiles!community_posts_user_id_fkey (id, first_name, last_name, avatar_url),
                replies_count: community_posts(count)
            `)
            .order('created_at', { ascending: false });

        if (parentId) {
            query = query.eq('parent_id', parentId);
        } else {
            query = query.is('parent_id', null);
        }

        const { data, error } = await query;
        if (error) throw error;

        return (data || []).map((post: any) => ({
            ...post,
            replies_count: Array.isArray(post.replies_count)
                ? post.replies_count[0]?.count || 0
                : typeof post.replies_count === 'object'
                    ? post.replies_count?.count || 0
                    : post.replies_count || 0
        }));
    },

    create: async (userId: string, content: string, parentId: string | null = null) => {
        const { data, error } = await supabase
            .from('community_posts')
            .insert([{ user_id: userId, content, parent_id: parentId }])
            .select(`
                *,
                profiles!community_posts_user_id_fkey (id, first_name, last_name, avatar_url)
            `)
            .single();

        if (error) throw error;
        return data;
    },

    like: async (userId: string, postId: string) => {
        const { error } = await supabase
            .from('community_likes')
            .insert([{ user_id: userId, post_id: postId }]);
        if (error) throw error;
        return true;
    },

    unlike: async (userId: string, postId: string) => {
        const { error } = await supabase
            .from('community_likes')
            .delete()
            .match({ user_id: userId, post_id: postId });
        if (error) throw error;
        return true;
    },

    getLikesByUser: async (userId: string) => {
        const { data, error } = await supabase
            .from('community_likes')
            .select('post_id')
            .eq('user_id', userId);
        if (error) throw error;
        return data.map(l => l.post_id);
    },

    toggleLike: async (userId: string, postId: string, currentlyLiked: boolean) => {
        if (currentlyLiked) {
            return await communityApi.unlike(userId, postId);
        } else {
            return await communityApi.like(userId, postId);
        }
    },

    reportPost: async (userId: string, postId: string, reason: string) => {
        const { error } = await supabase
            .from('community_reports')
            .insert([{ reporter_id: userId, post_id: postId, reason }]);
        if (error) throw error;
        return true;
    },

    listReports: async () => {
        const { data, error } = await supabase
            .from('community_reports')
            .select(`
                *,
                community_posts (*, profiles!community_posts_user_id_fkey(first_name, last_name, avatar_url)),
                reporter:profiles!community_reports_reporter_id_fkey(first_name, last_name, avatar_url)
            `)
            .order('created_at', { ascending: false });
        if (error) throw error;
        return data;
    },

    updateReportStatus: async (reportId: string, status: 'pending' | 'reviewed' | 'dismissed' | 'deleted') => {
        const { error } = await supabase
            .from('community_reports')
            .update({ status })
            .eq('id', reportId);
        if (error) throw error;
        return true;
    },

    deletePostWithReports: async (postId: string) => {
        // Update reports to 'deleted'
        await supabase.from('community_reports').update({ status: 'deleted' }).eq('post_id', postId);
        // Delete post
        const { error } = await supabase.from('community_posts').delete().eq('id', postId);
        if (error) throw error;
        return true;
    },

    deletePost: async (postId: string) => {
        const { error } = await supabase.from('community_posts').delete().eq('id', postId);
        if (error) throw error;
        return true;
    }
};

export const profilesApi = {
    get: async (id: string) => {
        const { data, error } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', id)
            .single();
        if (error) throw error;
        return data;
    },

    listAll: async () => {
        const { data, error } = await supabase
            .from('profiles')
            .select('id, first_name, last_name, avatar_url');
        if (error) throw error;
        return data || [];
    },

    update: async (id: string, updates: any) => {
        const { error } = await supabase
            .from('profiles')
            .update(updates)
            .eq('id', id);
        if (error) throw error;
        return true;
    },

    listFollowed: async (followerId: string) => {
        const { data, error } = await supabase
            .from('follows')
            .select('following_id, profiles!follows_following_id_fkey(*)')
            .eq('follower_id', followerId);
        if (error) throw error;
        return data.map(d => d.profiles);
    },

    listSuggested: async (userId: string) => {
        // Fetch users the current user is already following
        const { data: followed } = await supabase
            .from('follows')
            .select('following_id')
            .eq('follower_id', userId);

        const followedIds = followed?.map(f => f.following_id) || [];
        followedIds.push(userId); // Exclude self

        const { data, error } = await supabase
            .from('profiles')
            .select('*')
            .not('id', 'in', `(${followedIds.join(',')})`)
            .limit(5);

        if (error) throw error;
        return data || [];
    },

    follow: async (followerId: string, followingId: string) => {
        const { error } = await supabase
            .from('follows')
            .insert([{ follower_id: followerId, following_id: followingId }]);
        if (error) throw error;
        return true;
    },

    unfollow: async (followerId: string, followingId: string) => {
        const { error } = await supabase
            .from('follows')
            .delete()
            .match({ follower_id: followerId, following_id: followingId });
        if (error) throw error;
        return true;
    }
};

export const systemPagesSeoApi = {
    list: async () => {
        const { data, error } = await supabase.from('system_pages_seo').select('*').order('slug');
        if (error) throw error;
        return data || [];
    },
    getBySlug: async (slug: string) => {
        const { data, error } = await supabase.from('system_pages_seo').select('*').eq('slug', slug).maybeSingle();
        if (error) throw error;
        return data;
    },
    update: async (id: string, updates: any) => {
        const { error } = await supabase.from('system_pages_seo').update(updates).eq('id', id);
        if (error) throw error;
        return true;
    },
    create: async (data: any) => {
        const { data: page, error } = await supabase.from('system_pages_seo').insert([data]).select().single();
        if (error) throw error;
        return page;
    },
    delete: async (id: string) => {
        const { error } = await supabase.from('system_pages_seo').delete().eq('id', id);
        if (error) throw error;
        return { ok: true };
    }
};
